import { useCallback, useEffect, useRef, useState } from 'react';

/** Draw-or-type signature capture. Exposes its PNG through onReady. */
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [mode, setMode] = useState<'draw' | 'type' | 'upload'>('draw');
  const [typed, setTyped] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    onChange(null);
  }, [onChange]);

  // Typed mode renders the name in a script face onto the same canvas.
  useEffect(() => {
    if (mode !== 'type') return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!typed.trim()) { onChange(null); return; }
    ctx.font = 'italic 42px "Segoe Script", "Brush Script MT", cursive';
    ctx.fillStyle = '#1e293b';
    ctx.textBaseline = 'middle';
    ctx.fillText(typed.trim(), 16, canvas.height / 2, canvas.width - 32);
    onChange(canvas.toDataURL('image/png'));
  }, [mode, typed, onChange]);

  /** An uploaded image (PNG/JPG) is drawn onto the canvas scaled to fit,
   *  so whatever the source, what leaves the pad is always a clean PNG -
   *  the same shape the drawn and typed modes produce. */
  function handleUpload(picked: File | null) {
    setUploadError(null);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!picked || !canvas || !ctx) return;
    if (!picked.type.startsWith('image/')) {
      setUploadError('Choose an image file (PNG or JPG).');
      return;
    }
    const url = URL.createObjectURL(picked);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / image.width, canvas.height / image.height, 1);
      const w = image.width * scale;
      const h = image.height * scale;
      ctx.drawImage(image, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      onChange(canvas.toDataURL('image/png'));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      setUploadError('Could not read that image - try another file.');
    };
    image.src = url;
  }

  function pointAt(e: React.PointerEvent<HTMLCanvasElement>): [number, number] {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) / rect.width) * canvas.width,
      ((e.clientY - rect.top) / rect.height) * canvas.height,
    ];
  }

  return (
    <div>
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
        {(['draw', 'type', 'upload'] as const).map((m) => (
          <button key={m} type="button"
            onClick={() => { setMode(m); clear(); setTyped(''); setUploadError(null); }}
            className={[
              'rounded-md px-3 py-1.5 capitalize transition',
              mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
            ].join(' ')}>
            {m}
          </button>
        ))}
      </div>

      {mode === 'type' && (
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Type your full name"
          maxLength={60}
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
      )}

      {mode === 'upload' && (
        <div className="mt-2">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => handleUpload(e.target.files?.[0] ?? null)}
            className="w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
          />
          {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
          <p className="mt-1 text-xs text-slate-400">
            A photo or scan of your signature - it is fitted into the box below.
          </p>
        </div>
      )}

      <div className="relative mt-2">
        <canvas
          ref={canvasRef}
          width={560}
          height={160}
          onPointerDown={(e) => {
            if (mode !== 'draw') return;
            drawing.current = true;
            const ctx = canvasRef.current!.getContext('2d')!;
            const [x, y] = pointAt(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
            (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (mode !== 'draw' || !drawing.current) return;
            const ctx = canvasRef.current!.getContext('2d')!;
            const [x, y] = pointAt(e);
            ctx.lineWidth = 2.5;
            ctx.lineCap = 'round';
            ctx.strokeStyle = '#1e293b';
            ctx.lineTo(x, y);
            ctx.stroke();
            hasInk.current = true;
          }}
          onPointerUp={() => {
            if (mode !== 'draw') return;
            drawing.current = false;
            if (hasInk.current) onChange(canvasRef.current!.toDataURL('image/png'));
          }}
          className="w-full touch-none rounded-lg border border-dashed border-slate-300 bg-white"
        />
        <span className="pointer-events-none absolute bottom-2 left-3 text-[11px] text-slate-300">
          {mode === 'draw' ? 'Sign above with your mouse or finger' : 'Preview'}{/* upload + type both preview here */}
        </span>
      </div>
      <button type="button" onClick={() => { clear(); setTyped(''); }}
        className="mt-1.5 text-xs font-medium text-slate-500 hover:text-slate-700">
        Clear
      </button>
    </div>
  );
}
